import React, { useState } from 'react';
import { Mail, Phone, Briefcase, Award, ChevronDown, ChevronUp, Star, Copy, Check, CheckSquare, Square, Download, Loader2 } from 'lucide-react';
import { downloadIndividualResume } from '../../services/api';

const CandidateCard = ({ candidate, index, isSelected, onToggleSelect, showCheckbox = false }) => {
  const [expanded, setExpanded] = useState(index < 2);
  const [copied, setCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-br from-yellow-400 to-orange-500';
    if (rank === 2) return 'bg-gradient-to-br from-gray-300 to-gray-400';
    if (rank === 3) return 'bg-gradient-to-br from-orange-400 to-red-500';
    return 'bg-gradient-to-br from-blue-500 to-purple-500';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const handleCopyEmail = () => {
    if (candidate.candidateEmail) {
      navigator.clipboard.writeText(candidate.candidateEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Download individual resume
  const handleDownloadResume = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setIsDownloading(true);
      await downloadIndividualResume(candidate.resumeId);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download resume. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const SkillBar = ({ label, percentage }) => (
    <div className="mb-3">
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm font-bold text-purple-600">{percentage}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-1000"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className={`bg-white rounded-2xl shadow-lg border-2 transition-all hover:shadow-2xl relative ${
      isSelected ? 'border-purple-500 ring-4 ring-purple-100' : 'border-gray-100'
    }`}>
      {/* Selection Checkbox (Top Right) */}
      {showCheckbox && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect();
            }}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
              isSelected
                ? 'bg-purple-600 text-white shadow-lg scale-110'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
          >
            {isSelected ? <CheckSquare className="w-6 h-6" /> : <Square className="w-6 h-6" />}
          </button>
        </div>
      )}

      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {/* Rank Badge */}
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold text-white shadow-lg ${getRankBadgeColor(candidate.rank)}`}>
              {candidate.rank === 1 && '🏆'}
              {candidate.rank === 2 && '🥈'}
              {candidate.rank === 3 && '🥉'}
              {candidate.rank > 3 && `#${candidate.rank}`}
            </div>

            {/* Candidate Info */}
            <div className={showCheckbox ? 'pr-12' : ''}>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {candidate.candidateName || 'Name not found'}
              </h3>
              <p className="text-gray-600 flex items-center gap-2">
                {candidate.currentDesignation && (
                  <>
                    <Briefcase className="w-4 h-4" />
                    {candidate.currentDesignation}
                  </>
                )}
                {candidate.currentDesignation && candidate.currentCompany && ' • '}
                {candidate.currentCompany}
              </p>
            </div>
          </div>

          {/* Match Score & Quick Actions */}
          {!showCheckbox && (
            <div className="text-right flex flex-col items-end gap-3">
              <div>
                <div className={`text-5xl font-bold ${getScoreColor(candidate.matchScore)} mb-1`}>
                  {Math.round(candidate.matchScore)}%
                </div>
                <div className="flex items-center gap-1 justify-end">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.round(candidate.matchScore / 20)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Match Score</p>
              </div>
              {/* Quick Download Button */}
              <button
                onClick={handleDownloadResume}
                disabled={isDownloading}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium text-sm hover:shadow-md transition flex items-center gap-2 disabled:opacity-50"
                title="Download Resume"
              >
                {isDownloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Resume</span>
              </button>
            </div>
          )}
        </div>

        {/* Match Score for Checkbox Mode */}
        {showCheckbox && (
          <div className="mt-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Match Score</span>
                <span className={`text-2xl font-bold ${getScoreColor(candidate.matchScore)}`}>
                  {Math.round(candidate.matchScore)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${
                    candidate.matchScore >= 80 ? 'bg-green-500' :
                    candidate.matchScore >= 70 ? 'bg-blue-500' :
                    candidate.matchScore >= 60 ? 'bg-yellow-500' :
                    'bg-orange-500'
                  }`}
                  style={{ width: `${candidate.matchScore}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div className="p-6 bg-gray-50 grid grid-cols-3 gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-500 mb-1">Email</p>
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-800 truncate">{candidate.candidateEmail || 'Not provided'}</p>
              {candidate.candidateEmail && (
                <button
                  onClick={handleCopyEmail}
                  className="flex-shrink-0 p-1 hover:bg-blue-200 rounded transition"
                  title="Copy email"
                >
                  {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3 text-blue-600" />}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Phone className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Phone</p>
            <p className="text-sm text-gray-800">{candidate.candidatePhone || 'Not provided'}</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Award className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Experience</p>
            <p className="text-sm text-gray-800 font-semibold">
              {candidate.yearsOfExperience ? `${candidate.yearsOfExperience} years` : 'Not specified'}
            </p>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <div className="p-6">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between text-left mb-4 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
        >
          <span className="font-semibold text-gray-700">
            {expanded ? 'Hide' : 'Show'} Detailed Analysis
          </span>
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>

        {expanded && (
          <div className="space-y-4 animate-fadeIn">
            {/* Skills Match Visualization */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <Award className="w-4 h-4 text-white" />
                </div>
                Skills Assessment
              </h4>
              <SkillBar label="Technical Match" percentage={Math.round(candidate.matchScore)} />
              <SkillBar label="Experience Level" percentage={Math.min((candidate.yearsOfExperience || 0) * 10, 100)} />
              <SkillBar label="Overall Fit" percentage={Math.round(candidate.matchScore * 0.95)} />
            </div>

            {/* Analysis Sections */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h4 className="text-sm font-bold text-green-700 mb-2 flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  Matching Skills
                </h4>
                <p className="text-sm text-green-800 leading-relaxed">{candidate.matchingSkills || 'N/A'}</p>
              </div>

              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <h4 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                  <div className="w-6 h-6 bg-red-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xs">✗</span>
                  </div>
                  Missing Skills
                </h4>
                <p className="text-sm text-red-800 leading-relaxed">{candidate.missingSkills || 'N/A'}</p>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="text-sm font-bold text-blue-700 mb-2 flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-600" />
                  Key Strengths
                </h4>
                <p className="text-sm text-blue-800 leading-relaxed">{candidate.strengths || 'N/A'}</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h4 className="text-sm font-bold text-orange-700 mb-2 flex items-center gap-2">
                  <span className="text-orange-600">⚠️</span>
                  Areas for Improvement
                </h4>
                <p className="text-sm text-orange-800 leading-relaxed">{candidate.weaknesses || 'N/A'}</p>
              </div>
            </div>

            {/* AI Analysis */}
            <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
              <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm">🤖</span>
                </div>
                AI Recruiter Insights
              </h4>
              <p className="text-purple-900 leading-relaxed italic">
                "{candidate.aiAnalysis || 'N/A'}"
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleDownloadResume}
                disabled={isDownloading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-green-200 transform hover:-translate-y-0.5 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Download Resume
                  </>
                )}
              </button>
              <button className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-200 transform hover:-translate-y-0.5 transition flex items-center justify-center gap-2">
                <Mail className="w-5 h-5" />
                Contact Candidate
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CandidateCard;